import { lazy, Suspense, useEffect, useState } from 'react';
import { Button, Input, Select, useDisclosure, useToast } from '@chakra-ui/react';
import { HttpStatusCode } from 'axios';
import { useNavigate } from 'react-router-dom';

import AddIcon from '@/assets/AddIcon';
import ModalDelete from '@/components/Modals/ModalDelete';
import Loader from '@/components/Layouts/Loader';
import { DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE, DURATION_TOAST, COLORS } from '@/common/constants';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { RootState } from '@/redux/store';
import { setDataTotalNewInspectionFee } from '@/redux/slices/notificationSlice';
import { deleteInspectionFee, getListInspectionFee } from '@/services/inspectionFeeService';
import { getNewRecordCounterSuperAdmin } from '@/services/newRecordCounterService';

import './styles/InspectionFeeList.css';
import '../styles/ListPage.css';

const TableInspectionFee = lazy(() => import('@/components/Tables/TableInspectionFee'));

const InspectionFeeList = () => {
    const navigate = useNavigate();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const toast = useToast();
    const name = useAppSelector((state: RootState) => state.account.name);
    const dispatch = useAppDispatch();

    const [currentPageNumber, setCurrentPageNumber] = useState(DEFAULT_PAGE_NUMBER);
    const [currentPageSize, setCurrentPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [dataList, setDataList] = useState([]);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [idToDelete, setIdToDelete] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [searchValue, setSearchValue] = useState('');
    const [statusValue, setStatusValue] = useState('');
    const [typeValue, setTypeValue] = useState('');
    const [totalPageNumber, setTotalPageNumber] = useState(1);

    const handleChange = (setter: React.Dispatch<React.SetStateAction<any>>) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        setter(e.target.value);
    };

    const showErrorToast = (error: { status?: string; code?: string; message: string }) => {
        toast({
            title: error.status || 'Error',
            description: error.message,
            status: 'error',
            duration: DURATION_TOAST,
            isClosable: true
        });
    };

    const handleDelete = (id: string) => {
        setIdToDelete(id);
        onOpen();
    };

    const handleConfirmDelete = async () => {
        const response = await deleteInspectionFee({ id: idToDelete, modifiedBy: name || "System" }) as any;
        if (response.status === HttpStatusCode.Ok) {
            await getData();
            toast({
                title: 'Inspection fee deleted.',
                description: "We've deleted the inspection fee.",
                status: 'success',
                duration: DURATION_TOAST,
                isClosable: true,
            });
        } else {
            showErrorToast(response || { message: 'Failed to delete inspection fee.' });
        }
        onClose();
    };

    const handleSearch = () => {
        setCurrentPageNumber(1);
        setTimeout(() => {
            getData();
        }, 300);
    };

    const setLatestCounter = async () => {
        const responseCounter = await getNewRecordCounterSuperAdmin() as any;
        if (responseCounter?.status === HttpStatusCode.Ok) {
            dispatch(setDataTotalNewInspectionFee(responseCounter?.data?.inspectionFee || 0));
        }
    }

    const getData = async () => {
        setIsLoading(true);
        const params = {
            search: searchValue,
            pageNumber: currentPageNumber,
            pageSize: currentPageSize,
            status: statusValue,
            type: typeValue
        };

        try {
            const response = await getListInspectionFee(params) as any;
            if (response.status === HttpStatusCode.Ok) {
                setDataList(response.data);
                setTotalPageNumber(response.totalPage);
                setHasNextPage(response.hasNextPage);
                setLatestCounter();
            } else {
                showErrorToast(response);
            }
        } catch (error) {
            showErrorToast({ message: 'An error occurred while fetching data.' });
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        getData();
    }, [currentPageSize, currentPageNumber]);

    return (
        <>
            <ModalDelete title="Delete it?" isOpen={isOpen} onClose={onClose} handleConfirmDelete={handleConfirmDelete} />
            <div className="table-container">
                <div className="table-filter-action-container">
                    <div className="table-filter-container">
                        <div className="filter-input-width-5">
                            <Input
                                placeholder='Search'
                                onChange={handleChange(setSearchValue)}
                            />
                        </div>
                        <div className="filter-input-width-3">
                            <Select placeholder='All Status' className="select-search" onChange={handleChange(setStatusValue)}>
                                <option value="true">Active</option>
                                <option value="false">Inactive</option>
                            </Select>
                        </div>
                        <div className="filter-input-width-3">
                            <Select placeholder='All Type' className="select-search" onChange={handleChange(setTypeValue)}>
                                <option value="SIRE">SIRE</option>
                                <option value="Vetting">Vetting</option>
                            </Select>
                        </div>
                        <div>
                            <Button
                                className='button table-search-button'
                                onClick={handleSearch}
                            >
                                Search
                            </Button>
                        </div>
                        <div className="table-filter-separator"/>
                        <Button
                            leftIcon={<AddIcon />}
                            bgColor={COLORS.BUTTON.BACKGROUND}
                            color={COLORS.BUTTON.FOREGROUND}
                            className='table-add-button'
                            variant='solid'
                            borderRadius={10}
                            onClick={() => navigate('/manage-inspection-fee/add')}
                        >
                            Add Inspection Fee
                        </Button>
                    </div>
                </div>
                <Suspense fallback={<Loader />}>
                    <TableInspectionFee
                        dataList={dataList}
                        isLoading={isLoading}
                        handleEdit={(id: string) => navigate('/manage-inspection-fee/edit', { state: { id } })}
                        handleDelete={handleDelete}
                        currentPageSize={currentPageSize}
                        handleChangePageSize={setCurrentPageSize}
                        handleChangePageNumber={setCurrentPageNumber}
                        currentPageNumber={currentPageNumber}
                        totalPageNumber={totalPageNumber}
                        hasNextPage={hasNextPage}
                    />
                </Suspense>
            </div>
        </>
    );
};

export default InspectionFeeList;
